import {Component, Inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ForecastMeasureOption, WeatherForecastComponent} from './weather-forecast-component';
import {APP_CONFIG_TOKEN, AppConfig} from '../../app.config';
import {ResizeService} from '../../shared/resize.service';
import {I18NService} from '../../i18n/i18n.service';
import {AuthService} from 'auth-commons';
import {SmagApiService} from '../../api/smag-api.service';
import {TimeFilterComponent} from '../../shared/time-filter/time-filter.component';
import {HierarchyEntity, HierarchyService} from '../../shared/hierarchy.service';
import {isDefined} from '../../commons/commons';
import {map, takeUntil} from 'rxjs/operators';
import {EnergyCommunity, Entity, EntityType} from 'smag-commons/cfg/hierarchy';
import {MeasureType as MeteoMeasureType} from 'smag-commons/cfg/connector/meteo';
import {Observable, of} from 'rxjs';
import {Measure} from 'smag-commons/dto/msg';

const EC_MANDATORY_MEASURES: MeteoMeasureType[] = [
    MeteoMeasureType.RADIATION,
    MeteoMeasureType.TEMPERATURE
];

@Component({
    selector: 'app-ec-weather-forecast',
    templateUrl: './ec-weather-forecast.component.html',
    styleUrls: ['./ec-weather-forecast.component.scss']
})
export class ECWeatherForecastComponent extends WeatherForecastComponent implements OnInit, OnDestroy {

    @ViewChild('timeFilter') protected timeFilterComponent: TimeFilterComponent;

    ecResource: HierarchyEntity | null; // `null` if not an energy community

    // exported in HTML
    isDefined = isDefined;

    constructor(@Inject(APP_CONFIG_TOKEN) protected readonly cfg: AppConfig,
                private readonly hierarchyService: HierarchyService,
                protected readonly resizeService: ResizeService,
                protected readonly i18n: I18NService,
                protected readonly auth: AuthService,
                protected readonly apiService: SmagApiService) {
        super(cfg, resizeService, i18n, auth, apiService);
    }

    ngOnInit() {
        this.onInit();
        // NOTE: an "initial" emission on `resourceSelectionSubj` is always guaranteed by "manually calling" `onSelectResource()`

        this.hierarchyService.selection.pipe(
            map(resource => resource?.type === EntityType.ENERGY_COMMUNITY ? resource : null),
            takeUntil(this.destroyed)
        ).subscribe(ecResource => {
            this.ecResource = ecResource;
            this.onSelectResource(ecResource?.id);
        });
    }

    ngOnDestroy() {
        this.onDestroy();
    }

    protected mkMeasureOptions(): Observable<ForecastMeasureOption[]> {
        if (this.resource?.type !== EntityType.ENERGY_COMMUNITY) {
            return of([]);
        }
        const {
            meteo_connector_id: meteoConnectorId,
            meteo_virtual_device_id: meteoVirtualDeviceId
        } = this.resource as Entity as EnergyCommunity;
        if (!meteoConnectorId || !meteoVirtualDeviceId) {
            return of([]);
        }
        return this.loadAvailableMeasures().pipe(
            map(availableMeasures => Object.keys(this.forecastMeasureSettings)
                .filter((measureType: MeteoMeasureType) => availableMeasures.includes(measureType) || EC_MANDATORY_MEASURES.includes(measureType))
                .map((measureType: MeteoMeasureType) => (({
                    name: this.forecastMeasureSettings[measureType].name,
                    dataFactory: this.mkMeasureDataFactory(measureType,
                        timeRange => this.loadMeasures(measureType, timeRange),
                        this.forecastMeasureSettings[measureType].defaultUom)
                } as ForecastMeasureOption))))
        );
    }

    private loadAvailableMeasures(): Observable<MeteoMeasureType[]> {
        return this.apiService.retrieveECWeatherAvailableMeasures(this.resource.id)
            .pipe(map(resp => resp.results));
    }

    private loadMeasures(measureType: MeteoMeasureType, timeRange: [number, number]): Observable<Measure[]> {
        const [startTime, endTime] = timeRange;
        return this.apiService.retrieveECWeatherMeasures(this.resource.id, measureType, startTime, endTime, null)
            .pipe(map(resp => resp.results));
    }
}

